/**
 * Shared TeamLogo Component
 * Displays an NBA team logo with abbreviation fallback.
 * Extracted from BoxScoreModal to avoid duplication across 4+ files.
 */

import type { LeagueKey } from '@parlay-picker/shared/league';
import { getTeamInfo } from '@parlay-picker/shared/teams';
import type { ReactElement } from 'react';

interface TeamLogoProps {
  /** Team name or abbreviation (matched via getTeamInfo) */
  name: string;
  /** League for team lookup */
  leagueKey: LeagueKey;
  /** Logo size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Additional CSS classes */
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
  xl: 'w-12 h-12',
};

const TEXT_SIZES = {
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-base',
};

export function TeamLogo({
  name,
  leagueKey,
  size = 'md',
  className = '',
}: TeamLogoProps): ReactElement {
  const info = getTeamInfo(name, leagueKey);
  const sizeClass = SIZE_CLASSES[size];
  const textSize = TEXT_SIZES[size];

  if (info?.logo) {
    return (
      <img src={info.logo} alt={info.abbr} className={`${sizeClass} object-contain ${className}`} />
    );
  }
  return (
    <div
      className={`${sizeClass} rounded-full bg-theme-surface-hover flex items-center justify-center ${textSize} font-bold text-theme-text-secondary ${className}`}
    >
      {info?.abbr ?? name.slice(0, 3).toUpperCase()}
    </div>
  );
}

/**
 * Simple abbreviation-only fallback (no logo lookup).
 * Used when leagueKey isn't available or for minimal display.
 */
export function TeamLogoFallback({
  abbr,
  size = 'lg',
  className = '',
}: {
  abbr: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}): ReactElement {
  const sizeClass = SIZE_CLASSES[size];
  const textSize = TEXT_SIZES[size];

  return (
    <div
      className={`${sizeClass} rounded-full bg-theme-border flex items-center justify-center ${textSize} font-bold text-theme-text-secondary ${className}`}
    >
      {abbr}
    </div>
  );
}
